import type { MessageDescriptor } from 'react-intl';
import type { SaveStepCounts, SaveStepStatus } from '@/types/saveSteps.types';

// The two settled statuses mark every sub-line of their step; the rest leave
// the list's own bullet in place.
export const COUNT_STATUS_ICON: Partial<Record<SaveStepStatus, string>> = {
  fail: 'fas fa-xmark',
  success: 'fas fa-check'
};

export const STEP_STATUS_ICON: Record<SaveStepStatus, string> = {
  disabled: 'fas fa-ban',
  fail: 'fas fa-circle-xmark',
  pending: 'fas fa-circle',
  processing: 'fas fa-spinner fa-spin',
  success: 'fas fa-circle-check'
};

// Root-group icons, mirroring the wizard sidebar's own icons for these
// sections. Used while previewing (before a save has actually started); once
// saving, the status icons above take over instead (see `isPreview` usage).
export const CATEGORY_ICON = {
  contextTargeting: 'fa-kit fa-solid-fa-audience-centric',
  creatives: 'fa-solid fa-image',
  locationsScreens: 'fa-solid fa-earth-americas',
  mainSettings: 'fa-solid fa-gear'
} as const;

// Worst-of ordering for the Locations/Screens root status: any non-disabled
// child status wins over 'disabled', so the group only reads as disabled when
// all three children are.
const STATUS_PRIORITY: SaveStepStatus[] = ['fail', 'processing', 'pending', 'success', 'disabled'];
export const getWorstStatus = (statuses: SaveStepStatus[]): SaveStepStatus =>
  STATUS_PRIORITY.find(status => statuses.includes(status)) ?? 'disabled';

export type CountAction = 'created' | 'removed' | 'updated';

export const COUNT_ACTIONS: CountAction[] = ['created', 'removed', 'updated'];

// Each sub-line is written out per item type and per plural form rather than
// composed from a noun and a sentence: languages that decline the noun, or word
// the sentence differently, need the whole copy to translate.
export const ATTRIBUTION_POI_COUNT_MESSAGES: Record<CountAction, MessageDescriptor> = {
  created: {
    defaultMessage: '{count, plural, one {# POI has been added} other {# POIs have been added}}',
    id: 'subHeader.saveProgress.venues.attribution.counts.created'
  },
  removed: {
    defaultMessage: '{count, plural, one {# POI has been removed} other {# POIs have been removed}}',
    id: 'subHeader.saveProgress.venues.attribution.counts.removed'
  },
  updated: {
    defaultMessage: '{count, plural, one {# POI has been updated} other {# POIs have been updated}}',
    id: 'subHeader.saveProgress.venues.attribution.counts.updated'
  }
};

export const PROXIMITY_POINT_COUNT_MESSAGES: Record<CountAction, MessageDescriptor> = {
  created: {
    defaultMessage: '{count, plural, one {# Point has been added} other {# Points have been added}}',
    id: 'subHeader.saveProgress.venues.proximity.counts.created'
  },
  removed: {
    defaultMessage: '{count, plural, one {# Point has been removed} other {# Points have been removed}}',
    id: 'subHeader.saveProgress.venues.proximity.counts.removed'
  },
  updated: {
    defaultMessage: '{count, plural, one {# Point has been updated} other {# Points have been updated}}',
    id: 'subHeader.saveProgress.venues.proximity.counts.updated'
  }
};

export const LOCATION_COUNT_MESSAGES: Record<CountAction, MessageDescriptor> = {
  created: {
    defaultMessage: '{count, plural, one {# Location has been added} other {# Locations have been added}}',
    id: 'subHeader.saveProgress.attributionZones.counts.created'
  },
  removed: {
    defaultMessage: '{count, plural, one {# Location has been removed} other {# Locations have been removed}}',
    id: 'subHeader.saveProgress.attributionZones.counts.removed'
  },
  updated: {
    defaultMessage: '{count, plural, one {# Location has been updated} other {# Locations have been updated}}',
    id: 'subHeader.saveProgress.attributionZones.counts.updated'
  }
};

export const ACTIVATION_AREA_COUNT_MESSAGES: Record<CountAction, MessageDescriptor> = {
  created: {
    defaultMessage: '{count, plural, one {# Area has been added} other {# Areas have been added}}',
    id: 'subHeader.saveProgress.screens.standard.counts.created'
  },
  removed: {
    defaultMessage: '{count, plural, one {# Area has been removed} other {# Areas have been removed}}',
    id: 'subHeader.saveProgress.screens.standard.counts.removed'
  },
  updated: {
    defaultMessage: '{count, plural, one {# Area has been updated} other {# Areas have been updated}}',
    id: 'subHeader.saveProgress.screens.standard.counts.updated'
  }
};

export const SCREEN_COUNT_MESSAGES: Record<CountAction, MessageDescriptor> = {
  created: {
    defaultMessage: '{count, plural, one {# Screen has been added} other {# Screens have been added}}',
    id: 'subHeader.saveProgress.screens.dooh.counts.created'
  },
  removed: {
    defaultMessage: '{count, plural, one {# Screen has been removed} other {# Screens have been removed}}',
    id: 'subHeader.saveProgress.screens.dooh.counts.removed'
  },
  updated: {
    defaultMessage: '{count, plural, one {# Screen has been updated} other {# Screens have been updated}}',
    id: 'subHeader.saveProgress.screens.dooh.counts.updated'
  }
};

export const CREATIVE_COUNT_MESSAGES: Record<CountAction, MessageDescriptor> = {
  created: {
    defaultMessage: '{count, plural, one {# creative has been added} other {# creatives have been added}}',
    id: 'subHeader.saveProgress.creatives.counts.created'
  },
  removed: {
    defaultMessage: '{count, plural, one {# creative has been removed} other {# creatives have been removed}}',
    id: 'subHeader.saveProgress.creatives.counts.removed'
  },
  updated: {
    defaultMessage: '{count, plural, one {# creative has been updated} other {# creatives have been updated}}',
    id: 'subHeader.saveProgress.creatives.counts.updated'
  }
};

// Count badge text for a group/subgroup header (e.g. "3 changes"), independent
// of what kind of items changed.
export const GROUP_CHANGES_COUNT_MESSAGE: MessageDescriptor = {
  defaultMessage: '{count, plural, one {# change} other {# changes}}',
  id: 'subHeader.saveProgress.group.changesCount'
};

// One line per action the save will take. Actions with nothing to do are left
// out rather than shown as zero.
export const getCountLines = (
  counts: SaveStepCounts,
  messages: Record<CountAction, MessageDescriptor>,
  formatMessage: (descriptor: MessageDescriptor, values: { count: number }) => string
) =>
  COUNT_ACTIONS.filter(action => counts[action] > 0).map(action => ({
    id: action,
    text: formatMessage(messages[action], { count: counts[action] })
  }));
